import React, { useState, useEffect, useCallback, useRef } from 'react';
import Icon from '../shared/Icon';
import Chip from '../shared/Chip';
import { Link } from 'react-router-dom';
import { useFlag } from '@unleash/proxy-client-react';
import { apiService } from '../../../services/api';
import { mergeFileStatuses } from '../../../utils/fileReviewStatus';
import { getDatasetBadge, getDatasetCountLine } from '../../../utils/datasetBadge';
import { isAwaitingReviewOnly, isDatasetActive } from '../../../utils/datasetReviewState';
import { REVIEW_REQUIRED_LABEL, reviewDataSourcesHref } from '../../../utils/reviewRequiredNotice';
import { showFullTextIfTruncated } from '../../../utils/truncationTooltip';
import AddDatasetMenu from './AddDatasetMenu';
import RowActionsMenu from '../shared/RowActionsMenu';
import Modal from './iteration1/Modal';
import './AvailableDatasets.css';

// PRD-1622: why a row in the Datasets tab can't be selected yet, or what a partly ready one means.
export function datasetTabTooltip(dataset) {
  const line = getDatasetCountLine(dataset);
  if (!dataset.isReady) {
    const reason = dataset.badge === 'failed' ? "None of its files processed, so you can't select it"
      : 'You can select it once a file is ready';
    return line ? `${dataset.name}: ${line}. ${reason}` : `${dataset.name}: ${reason}`;
  }
  if (line && dataset.badge && dataset.badge !== 'ready') {
    return `${dataset.name}: ${line}. Answers draw only on the ready files`;
  }
  return line ? `${dataset.name}: ${line}` : undefined;
}

/**
 * A dataset's files with their review state filled in. The file list reports
 * a document parked for field review as `uploaded` (shown as "Queued"); only
 * /jobs says `awaiting_review`. The jobs call is best-effort — the list alone
 * is still worth showing if it fails.
 */
const loadFilesWithReviewState = async (datasetId) => {
  const [filesResp, jobs] = await Promise.all([
    apiService.getDatasetFiles(datasetId),
    apiService.getDatasetJobs(datasetId).catch(() => null),
  ]);
  const items = filesResp?.items || [];
  // Only overlay onto a real list: job rows carry no id, size or mime type,
  // which these rows render, so they must not stand in for an empty list here.
  return items.length > 0 ? mergeFileStatuses(items, jobs) : items;
};

// PRD-962 (v13): map the server `badge` token to a compact sidebar pill class.
// `ready` shows no pill — the enabled checkbox already signals it, and a green
// chip on every row clutters the narrow list. No spinner here: v13 allows a
// progress affordance only inside the opened-dataset view.
// PRD-1359 deliberately adds no `review_needed` entry here. Review is surfaced
// by its own chip, driven by the COUNT bucket rather than the badge, because
// badge precedence is processing > failed > review: a dataset that is both
// parked and failed carries `action_needed` and a badge-keyed entry would never
// fire for it. With no entry, a purely-parked dataset (badge `review_needed`)
// renders no status pill and only the review chip, so the two never duplicate.
const SIDEBAR_BADGE_CLASS = {
  preparing: 'available-dataset-processing',
  action_needed: 'available-dataset-action-needed',
  failed: 'available-dataset-failed',
};

/**
 * A dataset created from an in-chat microphone recording (PRD-941). The backend marks
 * these with source=chat_recording on the dataset record; accept a couple of field
 * spellings so the FE is robust to the final backend shape.
 */
const isVoiceDataset = (d) =>
  d?.source === 'chat_recording' ||
  d?.dataset_source === 'chat_recording' ||
  d?.origin === 'chat_recording';

/**
 * Available Datasets Panel Component
 */
const AvailableDatasets = ({
  selectedDatasets,
  onSelectionChange,
  refreshKey,
  enableCsvHitlReview,
  submittedReviewJobs,
  onOpenReview,
  onAddClick,
  onRecordClick,
  onViewTranscript,
  onRetryDataset,
  recorderSlot,
  // PRD-977: IDs the active thread was last grounded with. When the parent
  // switches threads, resolve these against the loaded dataset list and
  // pre-populate the chips so the user's mental model ("the chip is selected,
  // so this dataset is active") stays in sync with what the backend uses for
  // grounding. The parent passes a fresh array (new reference) per thread.
  preselectIds,
  // PRD-1359: hand the loaded list up so the chat side can resolve a turn's
  // source dataset NAMES back to dataset records and read file_counts off them.
  // Called on every successful fetch, poll included, which is what makes the
  // banner's count update without a manual refresh.
  onDatasetsLoaded,
  // PRD-1571 R10: nobody changes a shared query's datasets from this panel.
  // The pinned ids show selected with "Set by the owner"; every other dataset
  // is unavailable. null (the default) leaves the panel as it is.
  lockedIds = null,
  // Shared Queries handoff (Datasets tab, participant only): the owner's name. The tab then lists
  // only the owner's datasets, checked and not selectable, under the Datasets Note.
  lockedOwner = null,
  // PRD-1622 (Ask ResAI Iteration 1): 'tab' is the Datasets tab — the whole row selects, every
  // status shows as one chip with its count line, files open from "View dataset" in a modal,
  // and there is no drag, expand or "+" (decision 4).
  variant = 'panel',
}) => {
  const tab = variant === 'tab';
  const managedBy = tab && lockedIds && lockedOwner ? lockedOwner : null;
  const [viewing, setViewing] = useState(null);
  // PRD-962: drive the sidebar status indicator off the server `badge` field.
  const statusBadgesEnabled = useFlag('dataset_status_badges');
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Track expanded datasets and their files
  const [expandedDataset, setExpandedDataset] = useState(null);
  const [datasetFiles, setDatasetFiles] = useState({});
  const [loadingFiles, setLoadingFiles] = useState({});
  const [isPageVisible, setIsPageVisible] = useState(typeof document === 'undefined' ? true : !document.hidden);
  const pollRef = useRef(null);
  // Latest expanded dataset id, accessible inside the stable fetch callback.
  const expandedRef = useRef(null);
  // Last-seen file_summary signature per dataset; used to skip no-op file refetches.
  const prevFileSummariesRef = useRef({});
  // Latest dataset count, readable from the stable fetch callback's catch
  // block without stale-closing on `datasets`.
  const datasetsCountRef = useRef(0);
  // One-shot retry timer for foreground refreshes that hit a transient blip.
  const retryTimerRef = useRef(null);
  // Indirection to the latest fetch fn so the catch block can re-invoke itself
  // without re-referencing the const it's being assigned to.
  const fetchRef = useRef(null);
  // Same indirection for the lift-up callback: fetchPublishedDatasets is a
  // useCallback with no deps on purpose (the poll effect depends on its
  // identity), so reading the prop through a ref keeps it that way and spares
  // the parent from having to memoize the handler.
  const onDatasetsLoadedRef = useRef(null);

  useEffect(() => {
    expandedRef.current = expandedDataset;
  }, [expandedDataset]);

  useEffect(() => {
    datasetsCountRef.current = datasets.length;
  }, [datasets]);

  useEffect(() => () => {
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
  }, []);

  // PRD-977: hydrate the chip selection from the thread's prior dataset_ids.
  // Runs when `preselectIds` changes (parent passes a new array on thread
  // switch) and datasets have loaded. Skips when the user already has chips
  // selected so it never clobbers a live in-progress selection.
  const lastAppliedPreselectRef = useRef(null);
  useEffect(() => {
    if (!Array.isArray(preselectIds) || preselectIds.length === 0) return;
    if (!datasets.length) return;
    if (selectedDatasets && selectedDatasets.length > 0) return;
    const signature = preselectIds.slice().sort().join('|');
    if (lastAppliedPreselectRef.current === signature) return;
    const resolved = preselectIds
      .map((id) => datasets.find((d) => d.id === id))
      .filter(Boolean);
    if (resolved.length === 0) return;
    lastAppliedPreselectRef.current = signature;
    onSelectionChange(resolved);
  }, [preselectIds, datasets, selectedDatasets, onSelectionChange]);

  const fetchPublishedDatasets = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      // Fetch all datasets by paginating (backend max page_size is 100)
      let allItems = [];
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        // The right sidebar shows working datasets. With the auto-publish flow
        // (dataset_auto_publish) ON, a dataset stays 'draft' while its files are
        // still processing and only flips to 'published' once ingestion
        // finishes — so we must include 'draft' here or in-progress uploads
        // vanish from the sidebar until they complete. Failed drafts are dropped
        // downstream by the `visible` filter; processing drafts render with the
        // spinner. Module-owned datasets (SOP Library) live in the chat footer
        // per PRD §Story 3 AC; `module_id=null` partitions them out.
        const response = await apiService.getDatasets({
          status: ['draft', 'published'],
          module_id: null,
          page_size: 100,
          page,
        });
        const items = response.items || [];
        allItems.push(...items);
        hasMore = items.length === 100 && allItems.length < response.total;
        page++;
      }
      // Show ready + in-progress datasets. Drop failed ones — EXCEPT (a) datasets
      // that are still queryable because ≥1 file is ready (PRD-958), and (b) voice
      // recordings, which surface a Failed state with one-tap retry (PRD-941).
      const visible = allItems
        .filter((d) => {
          const job = (d.job_status || '').toLowerCase();
          const failed = job === 'failed' || job === 'error';
          return !failed || d.queryable === true || isVoiceDataset(d);
        })
        .map((d) => {
          const job = (d.job_status || '').toLowerCase();
          const jobComplete = ['complete', 'completed'].includes(job);
          // PRD-958: gate querying on the backend `queryable` flag (true once
          // ≥1 file reaches READY), independent of overall batch job_status — a
          // dataset with ready + failed/stuck siblings is still queryable. Fall
          // back to the legacy job_status check if `queryable` isn't present.
          const queryable = typeof d.queryable === 'boolean' ? d.queryable : jobComplete;
          // PRD-959: an empty dataset (no files, no jobs) reports job_status=null
          // and queryable=false. Without this escape hatch the sidebar would
          // poll it forever as "processing" — same bug the Datasets card had.
          // Treat empty datasets as both ready (don't show the processing
          // spinner) and complete (stop the polling loop).
          const fileCount = d.file_summary?.total ?? 0;
          const isEmptyDataset = !job && fileCount === 0;
          return {
            ...d,
            isReady: queryable || isEmptyDataset,       // selectable / draggable once ≥1 file is ready
            isComplete: jobComplete || isEmptyDataset,  // whole batch finished — drives progress UI + polling
            isFailed: job === 'failed' || job === 'error',
            isVoice: isVoiceDataset(d),
          };
        });
      setDatasets(visible);
      onDatasetsLoadedRef.current?.(visible);

      // Piggyback: if the currently expanded dataset's file_summary changed since the
      // last poll, refresh its per-file list once. Avoids a second polling loop while
      // still updating the expanded pill in place as files complete.
      const id = expandedRef.current;
      if (id) {
        const fresh = visible.find((d) => d.id === id);
        if (fresh) {
          const sig = JSON.stringify(fresh.file_summary || {});
          if (prevFileSummariesRef.current[id] !== sig) {
            prevFileSummariesRef.current[id] = sig;
            try {
              const files = await loadFilesWithReviewState(id);
              setDatasetFiles((prev) => ({ ...prev, [id]: files }));
            } catch (e) {
              console.error(`Error refreshing files for expanded dataset ${id}:`, e);
            }
          }
        }
      }
    } catch (err) {
      console.error('Error fetching datasets:', err);
      // Don't blank an already-rendered list (post-publish refresh racing the
      // backend's tail-end ingest work) or surface errors during background
      // polls — the next successful poll picks up the new state.
      const haveExisting = datasetsCountRef.current > 0;
      if (!silent && !haveExisting) {
        setError('Failed to load datasets');
      }
      // Foreground refresh that failed but we already had data: retry once
      // after a short backoff so the user sees the new state without having
      // to hit refresh themselves.
      if (!silent && haveExisting) {
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        retryTimerRef.current = setTimeout(() => {
          retryTimerRef.current = null;
          fetchRef.current?.({ silent: true });
        }, 1500);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Keep the retry indirection pointed at the latest stable fetch fn.
  useEffect(() => {
    fetchRef.current = fetchPublishedDatasets;
  }, [fetchPublishedDatasets]);

  useEffect(() => {
    onDatasetsLoadedRef.current = onDatasetsLoaded;
  }, [onDatasetsLoaded]);

  // Initial load + refresh when parent bumps refreshKey
  useEffect(() => {
    fetchPublishedDatasets();
  }, [fetchPublishedDatasets, refreshKey]);

  // Pause polling when the browser tab is hidden; resume + refetch immediately on return.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const handler = () => {
      const visible = !document.hidden;
      setIsPageVisible(visible);
      if (visible) {
        fetchPublishedDatasets({ silent: true });
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [fetchPublishedDatasets]);

  // Poll while any dataset's batch is still progressing AND the tab is visible.
  // Keyed off completion (not queryability) so we keep refreshing ready_file_count
  // for a partially-ready dataset until its remaining files finish (PRD-958).
  // File-level refreshes piggyback on this poll via fetchPublishedDatasets (no second timer).
  useEffect(() => {
    // PRD-1359: the review term is load-bearing and the same trap PRD-1358 hit
    // in Data Sources. Document field review never touches `job_status`, so an
    // all-parked dataset reports `complete` and `isComplete` goes true — without
    // isDatasetActive the sidebar would stop polling the moment a batch parks,
    // and the Review required chip and the banner count would then sit stale
    // until the user reloaded by hand.
    const hasProcessing = datasets.some(
      (d) => (!d.isComplete && !d.isFailed) || isDatasetActive(d),
    );
    if (!hasProcessing || !isPageVisible) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return undefined;
    }
    if (pollRef.current) return undefined;
    pollRef.current = setInterval(() => {
      fetchPublishedDatasets({ silent: true });
    }, 5000);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [datasets, fetchPublishedDatasets, isPageVisible]);

  const fetchDatasetFiles = async (datasetId) => {
    if (datasetFiles[datasetId]) return; // Already loaded
    setLoadingFiles(prev => ({ ...prev, [datasetId]: true }));
    try {
      const files = await loadFilesWithReviewState(datasetId);
      setDatasetFiles(prev => ({ ...prev, [datasetId]: files }));
    } catch (err) {
      console.error(`Error fetching dataset files for ${datasetId}:`, err);
      setDatasetFiles(prev => ({ ...prev, [datasetId]: [] }));
    } finally {
      setLoadingFiles(prev => ({ ...prev, [datasetId]: false }));
    }
  };

  const handleDatasetToggle = (dataset, e) => {
    e.stopPropagation();
    if (lockedIds) return;
    const isSelected = selectedDatasets.some(d => d.id === dataset.id);
    if (isSelected) {
      onSelectionChange(selectedDatasets.filter(d => d.id !== dataset.id));
    } else {
      onSelectionChange([...selectedDatasets, dataset]);
    }
  };

  const handleDatasetExpand = (dataset) => {
    if (expandedDataset === dataset.id) {
      setExpandedDataset(null);
    } else {
      setExpandedDataset(dataset.id);
      fetchDatasetFiles(dataset.id);
    }
  };

  const isDatasetSelected = (dataset) => {
    return selectedDatasets.some(d => d.id === dataset.id);
  };

  // A connector-synced dataset (e.g. BambooHR) is a live table behind a virtual file
  // row with size_bytes 0 — there is no file, so "0 B" would read as an empty one.
  const formatFileSizeOrLive = (file) =>
    file.document_type === 'synced_table' ? 'Live' : formatFileSize(file.size_bytes);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatFileStatus = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'complete' || s === 'completed' || s === 'success') return 'Ready';
    if (s === 'failed' || s === 'error') return 'Failed';
    if (s === 'rejected') return 'Rejected';
    if (s === 'awaiting_input') return 'Needs review';
    // Document field review — the dataset chip's wording, so the two agree.
    if (s === 'awaiting_review') return 'Review required';
    if (s === 'uploaded' || s === 'queued' || s === 'pending') return 'Queued';
    if (s === 'processing' || s === 'extracting' || s === 'indexing' || s === 'embedding' || s === 'chunking' || s === 'parsing') return 'Processing';
    return status || 'Processing';
  };

  // A participant's tab lists only the owner's datasets; with none, it shows only the note.
  const listed = managedBy ? datasets.filter((d) => lockedIds.includes(d.id)) : datasets;

  // Filter and sort datasets
  const filteredDatasets = (() => {
    let result = [...listed];

    // Failed voice recordings are only kept for their retry affordance — if voice
    // capture isn't available to this user (no retry handler), don't surface them.
    if (!onRetryDataset) {
      result = result.filter((d) => !(d.isFailed && d.isVoice));
    }

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(d => (d.name || '').toLowerCase().includes(q));
    }

    // Default sort by recent
    result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    return result;
  })();

  return (
    <div className={`available-datasets${tab ? ' available-datasets--tab' : ''}`}>
      {!tab && (
      <div className="available-datasets-header">
        <h4 className="available-datasets-title">Available Datasets</h4>
        {onRecordClick ? (
          <AddDatasetMenu onUpload={onAddClick} onRecord={onRecordClick} />
        ) : onAddClick && (
          <button
            type="button"
            className="resui-btn resui-btn--secondary resui-btn--icon"
            onClick={onAddClick}
            aria-label="Upload file to create dataset"
            title="Upload file to create dataset"
          >
            <Icon name="add" />
          </button>
        )}
      </div>
      )}
      {(!tab || lockedIds) && !managedBy && (
      <p className="available-datasets-description">
        {lockedIds
          ? 'Datasets for a shared query are set by its owner.'
          : 'Select datasets to ask questions.'}
      </p>
      )}

      {/* Inline voice recorder widget (PRD-941) renders at the top of the selection. */}
      {recorderSlot}

      {/* Search Bar */}
      {!(managedBy && listed.length === 0) && (
      <div className="available-datasets-search-row">
        <input
          type="text"
          className="resui-input"
          placeholder={!loading && listed.length > 0 ? `Search ${listed.length} datasets...` : 'Search datasets...'}
          aria-label="Search datasets"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
      )}
      {/* Shared Queries handoff 2: the Datasets Note sits between the search and the rows. */}
      {managedBy && (
        <p className="iter1-side-note">{managedBy} manages the datasets for this query, you can’t change them.</p>
      )}

      {loading && (
        <div className="available-datasets-loading">Loading datasets...</div>
      )}

      {error && (
        <div className="available-datasets-error">{error}</div>
      )}

      {!loading && !error && datasets.length === 0 && !managedBy && (
        <div className="available-datasets-empty">
          No datasets available. Create a dataset to get started.
        </div>
      )}

      {!loading && !error && filteredDatasets.length === 0 && listed.length > 0 && (
        <div className="available-datasets-empty">
          No datasets match your search.
        </div>
      )}

      {!loading && !error && filteredDatasets.length > 0 && (
        <div className={`available-datasets-list${tab ? ' focus-room-list' : ''}`}>
          {filteredDatasets.map((dataset) => {
            const isSelected = isDatasetSelected(dataset);
            const isExpanded = expandedDataset === dataset.id;
            const files = datasetFiles[dataset.id] || [];
            const isLoadingFiles = loadingFiles[dataset.id];
            // PRD-962 (v13): server `badge` drives a single pill (Processing / Issues /
            // Failed) with the count line as a hover tooltip; no spinner in list rows.
            // Selection/drag gating stays on isReady (= queryable, PRD-958).
            const statusBadge = statusBadgesEnabled || tab ? getDatasetBadge(dataset) : null;
            const statusTooltip = tab ? datasetTabTooltip(dataset) : statusBadgesEnabled ? getDatasetCountLine(dataset) : null;
            const sidebarBadgeClass = statusBadge ? SIDEBAR_BADGE_CLASS[dataset.badge] : null;
            // PRD-1359: dataset-level review marker. Independent of
            // `statusBadgesEnabled` above — that flag governs the PRD-962 badge
            // rollout, not review, and the count bucket is populated either way.
            // Gated on the count rather than `hitl-field-review` for the same
            // reason the Review entry point in DataSources.jsx is; see the note
            // on ReviewRequiredBanner.
            const reviewRequired = isAwaitingReviewOnly(dataset);
            // Legacy path (flag off): soft Failed pill + processing spinner.
            const showLegacyFailed = !statusBadgesEnabled && !tab && dataset.isFailed && !dataset.isReady;
            const showLegacySpinner = !statusBadgesEnabled && !tab && !dataset.isComplete && !dataset.isFailed;
            const selectable = dataset.isReady && !lockedIds;

            return (
              <div
                key={dataset.id}
                className={`available-dataset-card ${isSelected ? 'selected' : ''} ${isExpanded ? 'expanded' : ''} ${dataset.isReady ? '' : 'processing'} ${lockedIds && !lockedIds.includes(dataset.id) ? 'unavailable' : ''}${managedBy ? ' available-dataset-card--managed' : ''}${tab ? ' focus-row' : ''}`}
                draggable={!tab && dataset.isReady && !lockedIds}
                onDragStart={(e) => {
                  if (!dataset.isReady) {
                    e.preventDefault();
                    return;
                  }
                  try {
                    e.dataTransfer.setData(
                      'application/x-resworkbench-dataset',
                      JSON.stringify(dataset),
                    );
                    e.dataTransfer.effectAllowed = 'copy';
                  } catch (err) {
                    console.error('Failed to set drag data:', err);
                  }
                }}
              >
                <div
                  className="available-dataset-header"
                  onClick={(e) => (tab ? (selectable && handleDatasetToggle(dataset, e)) : handleDatasetExpand(dataset))}
                  title={tab && !dataset.isReady && !managedBy ? statusTooltip : undefined}
                  data-tip={managedBy ? `Managed by ${managedBy}` : undefined}
                >
                  {/* A participant's checkbox stays focusable (aria-disabled), so its reason
                      reaches keyboard users as its tooltip. */}
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => { if (!managedBy) handleDatasetToggle(dataset, e); }}
                    onClick={(e) => e.stopPropagation()}
                    disabled={!managedBy && (!dataset.isReady || Boolean(lockedIds))}
                    aria-disabled={managedBy ? 'true' : undefined}
                    data-tip={managedBy ? `Managed by ${managedBy}` : undefined}
                    className="available-dataset-checkbox"
                    aria-label={`Use ${dataset.name}`}
                  />
                  {dataset.isVoice && (
                    <Icon
                      name="mic"
                      className="available-dataset-voice-icon"
                      size={16}
                      title="Voice recording"
                    />
                  )}
                  <div className="available-dataset-name" onMouseEnter={tab ? undefined : showFullTextIfTruncated} title={tab && !managedBy ? dataset.name : undefined}>{dataset.name}</div>
                  {lockedIds?.includes(dataset.id) && !managedBy && (
                    <Icon name="lock" size={16} className="available-dataset-lock" title="Set by the owner" />
                  )}
                  {dataset.paused_at && (
                    <Chip
                      className="available-dataset-paused"
                      title="Paused — resume from the Datasets page to query"
                    >
                      Paused
                    </Chip>
                  )}
                  {statusBadgesEnabled || tab ? (
                    sidebarBadgeClass && (
                      <Chip variant={statusBadge.variant} className={sidebarBadgeClass} title={(!managedBy && statusTooltip) || undefined}>
                        {statusBadge.label}
                      </Chip>
                    )
                  ) : (
                    <>
                      {/* Only flag a hard "Failed" when nothing is queryable — a dataset with
                          some ready files stays selectable (PRD-958). */}
                      {showLegacyFailed && (
                        <Chip variant="error" className="available-dataset-failed" title="Processing failed">
                          Failed
                        </Chip>
                      )}
                      {showLegacySpinner && (
                        <span
                          className="available-dataset-spinner"
                          title="Still processing"
                          aria-label="Processing"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                          </svg>
                        </span>
                      )}
                    </>
                  )}
                  {tab ? (
                    <span onClick={(e) => e.stopPropagation()}>
                      <RowActionsMenu ariaLabel={`Actions for ${dataset.name}`} triggerClassName="iter1-row-trigger" iconSize={16}>
                        {(close) => (
                          <button
                            type="button"
                            onClick={() => { close({ restoreFocus: true }); setViewing(dataset); fetchDatasetFiles(dataset.id); }}
                          >
                            <Icon name="description" size={16} /> View dataset
                          </button>
                        )}
                      </RowActionsMenu>
                    </span>
                  ) : (
                  <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                  )}
                </div>
                {lockedIds?.includes(dataset.id) && !managedBy && (
                  <div className="available-dataset-owner-note">Set by the owner</div>
                )}


                {/* PRD-1359: links through to Field Review for this dataset.
                    A plain link, NOT a chip — design's call once per-file labels
                    were ruled out. Figma 1032-4611 draws this link under each
                    FILE, which needs per-file review state the API does not
                    expose yet (PRD-1360); design's direction was to lift that
                    one link up to sit under the DATASET name and drop the
                    dataset-level chip entirely, rather than keep both.
                    Its own row, not inline beside the name, because this rail is
                    ~150px wide and a nowrap label collapses the dataset name to
                    zero width there — same placement as the CSV Review button
                    just below.
                    Ungated: whether field review needs its own permissions
                    capability is still an open question on PRD-1359, and Data
                    Sources does not gate its own Review entry point today, so
                    gating only here would be an invented rule. */}
                {reviewRequired && (
                  <Link
                    className="resui-link available-dataset-review-required"
                    to={reviewDataSourcesHref(dataset.id)}
                    onClick={(e) => e.stopPropagation()}
                    title={statusTooltip || undefined}
                  >
                    {REVIEW_REQUIRED_LABEL}
                  </Link>
                )}

                {enableCsvHitlReview && dataset.pipeline_summary?.awaiting_input > 0 && !submittedReviewJobs?.has(dataset.csv_job_id) && (
                  <button
                    type="button"
                    className="available-dataset-review-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onOpenReview) onOpenReview(dataset);
                    }}
                  >
                    <Icon name="rate_review" />
                    Review
                  </button>
                )}
                {enableCsvHitlReview && dataset.pipeline_summary?.awaiting_input > 0 && submittedReviewJobs?.has(dataset.csv_job_id) && (
                  <span className="available-dataset-review-submitted">
                    <Icon name="check_circle" />
                    Review submitted — processing
                  </span>
                )}

                {/* Voice recording: retry a failed job (reprocess stored audio) — PRD-941. */}
                {dataset.isVoice && dataset.isFailed && onRetryDataset && (
                  <button
                    type="button"
                    className="available-dataset-retry-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRetryDataset(dataset);
                    }}
                  >
                    <Icon name="refresh" />
                    Retry
                  </button>
                )}

                {/* Voice recording: read-only transcript once ready — PRD-941. */}
                {dataset.isVoice && dataset.isReady && onViewTranscript && (
                  <button
                    type="button"
                    className="available-dataset-transcript-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewTranscript(dataset);
                    }}
                  >
                    <Icon name="description" />
                    View transcript
                  </button>
                )}

                {/* Files Section */}
                {isExpanded && (
                  <div className="dataset-files-section">
                    <div className="dataset-files-header">
                      <span className="dataset-files-title">Files ({files.length})</span>
                    </div>

                    {isLoadingFiles ? (
                      <div className="dataset-files-loading">Loading files...</div>
                    ) : files.length === 0 ? (
                      <div className="dataset-files-empty">No files uploaded</div>
                    ) : (
                      <div className="dataset-files-list">
                        {files.map((file) => (
                          <div key={file.id} className="dataset-file-item">
                            <div className="dataset-file-icon">
                              {file.mime_type === 'application/pdf' ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                  <polyline points="14 2 14 8 20 8" />
                                  <line x1="16" y1="13" x2="8" y2="13" />
                                  <line x1="16" y1="17" x2="8" y2="17" />
                                  <polyline points="10 9 9 9 8 9" />
                                </svg>
                              ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                                  <polyline points="13 2 13 9 20 9" />
                                </svg>
                              )}
                            </div>
                            <div className="dataset-file-info">
                              <span className="dataset-file-name" title={file.file_name}>
                                {file.file_name}
                              </span>
                              <span className="dataset-file-meta">
                                {formatFileSizeOrLive(file)} • {formatFileStatus(file.status)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {tab && viewing && (
        <Modal
          title={viewing.name}
          onClose={() => setViewing(null)}
          actions={(
            <button type="button" className="resui-btn resui-btn--secondary resui-btn--small" onClick={() => setViewing(null)}>
              Close
            </button>
          )}
        >
          {getDatasetCountLine(viewing) && <p className="iter1-modal-lead">{getDatasetCountLine(viewing)}</p>}
          {loadingFiles[viewing.id] ? (
            <p>Loading files…</p>
          ) : (datasetFiles[viewing.id] || []).length === 0 ? (
            <p>No files uploaded.</p>
          ) : (
            <ul className="iter1-file-list" aria-label={`Files in ${viewing.name}`}>
              {(datasetFiles[viewing.id] || []).map((file) => (
                <li key={file.id} className="iter1-file">
                  <span className="iter1-file-name" title={file.file_name}>{file.file_name}</span>
                  <span className="iter1-file-meta">{formatFileSizeOrLive(file)} · {formatFileStatus(file.status)}</span>
                </li>
              ))}
            </ul>
          )}
        </Modal>
      )}
    </div>
  );
};

export default AvailableDatasets;
